/**
 * Public home page showing recent published essays.
 * 
 * Features:
 * - Hero section with site introduction
 * - Grid of recent published essays
 * - Links to categories and tags
 * - Beautiful dark theme design
 * 
 * Called by:
 * - App.tsx as the default public route (/)
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Typography, Spin, Alert, Card, Tag, Space, Row, Col } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getPublicEssays, getPublicCategories, type PublicEssayListItem } from '../../services/api';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;

/**
 * Essay card component for the home page grid.
 */
function EssayCard({ essay }: { essay: PublicEssayListItem }) {
  return (
    <Link to={`/essays/${essay.slug}`} style={{ textDecoration: 'none' }}>
      <Card
        hoverable
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          height: '100%',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '24px' }}
        className="essay-card"
      >
        {/* Category badge */}
        {essay.category && (
          <Tag
            style={{
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: '#d4af37',
              marginBottom: '12px',
            }}
          >
            {essay.category.name}
          </Tag>
        )}

        {/* Title */}
        <Title
          level={4}
          style={{
            color: '#f5f5f5',
            marginBottom: '8px',
            fontFamily: "var(--font-serif)",
          }}
        >
          {essay.title}
        </Title>

        {/* Preview text */}
        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{
            color: '#a0a0a0',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            marginBottom: '16px',
          }}
        >
          {essay.preview}
        </Paragraph>

        {/* Meta info */}
        <Space size="middle" style={{ color: '#666' }}>
          {essay.publishedAt && (
            <span>
              <CalendarOutlined style={{ marginRight: '4px' }} />
              {format(new Date(essay.publishedAt), 'MMM d, yyyy')}
            </span>
          )}
          <span>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {essay.readingTime} min read
          </span>
        </Space>

        {/* Tags */}
        {essay.tags.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {essay.tags.slice(0, 3).map((tag) => (
              <Tag
                key={tag.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#888',
                  fontSize: '0.75rem',
                }}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        )}
      </Card>
    </Link>
  );
}

/**
 * Public home page component.
 */
function PublicHomePage() {
  // Fetch recent essays
  const {
    data: essaysData,
    isLoading: essaysLoading,
    error: essaysError,
  } = useQuery({
    queryKey: ['public', 'essays', 'recent'],
    queryFn: () => getPublicEssays({ limit: 6 }),
  });

  // Fetch categories
  const {
    data: categories,
  } = useQuery({
    queryKey: ['public', 'categories'],
    queryFn: getPublicCategories,
  });

  return (
    <div>
      {/* Hero section */}
      <section style={{ textAlign: 'center', marginBottom: '64px' }}>
        <Title
          style={{
            fontSize: '3rem',
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            color: '#f5f5f5',
            marginBottom: '16px',
            letterSpacing: '2px',
          }}
        >
          Compendium of All My Knowledge
        </Title>
        <Paragraph
          style={{
            fontSize: '1.2rem',
            color: '#a0a0a0',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.8,
          }}
        >
          A personal collection of essays exploring ideas, knowledge, and the
          endless pursuit of understanding.
        </Paragraph>
      </section>

      {/* Recent essays section */}
      <section style={{ marginBottom: '64px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <Title
            level={2}
            style={{
              color: '#f5f5f5',
              fontFamily: "var(--font-serif)",
              margin: 0,
            }}
          >
            Recent Essays
          </Title>
          <Link
            to="/essays"
            style={{
              color: '#d4af37',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            View all <ArrowRightOutlined />
          </Link>
        </div>

        {essaysLoading && (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spin size="large" />
          </div>
        )}

        {essaysError && (
          <Alert
            message="Error loading essays"
            description="Unable to load essays. Please try again later."
            type="error"
            showIcon
          />
        )}

        {essaysData && essaysData.essays.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Text type="secondary">No published essays yet. Check back soon!</Text>
          </div>
        )}

        {essaysData && essaysData.essays.length > 0 && (
          <Row gutter={[24, 24]}>
            {essaysData.essays.map((essay) => (
              <Col key={essay.id} xs={24} md={12} lg={8}>
                <EssayCard essay={essay} />
              </Col>
            ))}
          </Row>
        )}
      </section>

      {/* Categories section */}
      {categories && categories.length > 0 && (
        <section>
          <Title
            level={2}
            style={{
              color: '#f5f5f5',
              fontFamily: "var(--font-serif)",
              marginBottom: '24px',
            }}
          >
            Explore by Category
          </Title>

          <Row gutter={[16, 16]}>
            {categories.map((category) => (
              <Col key={category.id}>
                <Link to={`/category/${category.slug}`} style={{ textDecoration: 'none' }}>
                  <Card
                    hoverable
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '16px 24px' }}
                  >
                    <Space>
                      <Text strong style={{ color: '#f5f5f5' }}>
                        {category.name}
                      </Text>
                      <Tag
                        style={{
                          background: 'rgba(212, 175, 55, 0.15)',
                          border: 'none',
                          color: '#d4af37',
                        }}
                      >
                        {category.essayCount}
                      </Tag>
                    </Space>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        </section>
      )}

      {/* CSS for hover effects */}
      <style>{`
        .essay-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicHomePage;
